import React, { ReactElement, useEffect, useState } from 'react';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import { CourseLibraryPermissions } from '@/lib/auth/permissions/courseLibraryPermissions.enum';
import {
  Link,
  ListTable,
  MenuIcon,
  Modal,
  RemoveRedEyeOutlinedIcon,
  SaveAltIcon,
  Spinner,
} from 'elmo-elements';
import {
  useGetDownloadCourseResultQuery,
  useGetVideoLibraryElearningsQuery,
  useStartDownloadCourseMutation,
} from '@/modules/services';
import { getQueryString, sanitizeHTML, pushParamsToUrl } from '@/lib/util';
import { useDispatch } from 'react-redux';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import { useHistory, useLocation } from 'react-router-dom';
import { CourseLibraryDownloadResponse } from '@/pages/CourseLibrary/types';
import { CourseLibraryDefaultQueryString } from '@/pages/CourseLibrary/CourseLibraryRoutes';
import { Translations } from '@/lib/translation';
import { ModalContext } from '@/pages/CourseLibrary/components/List';

const tableColumns = {
  name: { label: 'Name' },
  category: { label: 'Category' },
  modifiedDate: { label: 'Last Modified' },
};

const sortableColumns = [
  { key: 'name', name: 'Name' },
  { key: 'category_id', name: 'Category' },
  { key: 'modified', name: 'Last Modified' },
];

const VideoLibraryList = (): ReactElement => {
  const history = useHistory();
  const location = useLocation();
  const pageSize = 10;

  const dispatch = useDispatch();
  const currentQueryString = getQueryString(
    location.search,
    CourseLibraryDefaultQueryString,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<string>('');
  const [modalContext, setModalContext] = useState<ModalContext | null>(null);
  const [skip, setSkip] = useState<boolean>(true);
  const [courseIdDownload, setCourseIdDownload] = useState<string>('');

  const canDownloadVideoLibrary = useUserHasCapabilities([
    CourseLibraryPermissions.DOWNLOAD_COURSE_LIBRARY,
  ]);
  const { data: videoLibraryData } =
    useGetVideoLibraryElearningsQuery(currentQueryString);
  const downloadResult = useGetDownloadCourseResultQuery(courseIdDownload, {
    pollingInterval: 2000,
    skip,
  });
  const [downloadCourse, downloadCourseStatus] =
    useStartDownloadCourseMutation();

  const handleLearnMoreClick = (info = '') => {
    setIsModalOpen(true);
    setModalContext(ModalContext.LEARN_MORE);
    setModalContent(sanitizeHTML(info) as unknown as string);
  };

  const handleDownloadCourse = (courseId: string) => {
    setCourseIdDownload(courseId);
    downloadCourse(courseId);
  };

  const handleSort = (column: number, direction: string) => {
    pushParamsToUrl(
      {
        sort: sortableColumns[column].key,
        order: direction.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  useEffect(() => {
    if (downloadResult.isError) {
      setSkip(true);
      setCourseIdDownload('');
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message:
            'An error occurred and we were unable to copy this module. Please try again.',
        },
      });
    }

    // Using isFetching because of caching issue for suceeding publish request w/o refreshing page.
    if (
      !downloadResult.isFetching &&
      downloadResult.isSuccess &&
      downloadResult.data
    ) {
      setSkip(true);
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Copy course module complete! Please verify in My Library.`,
        },
      });
      setCourseIdDownload('');
      if (
        isModalOpen &&
        modalContext === ModalContext.COPY_COURSE_IN_PROGRESS
      ) {
        setIsModalOpen(false);
      }
    }
  }, [downloadResult, dispatch, history, isModalOpen, modalContext]);

  useEffect(() => {
    if (downloadCourseStatus.isError) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message:
            'data' in downloadCourseStatus.error
              ? (
                  downloadCourseStatus.error
                    .data as CourseLibraryDownloadResponse
                ).message
              : 'Something went wrong with the copy',
        },
      });
    }
    if (
      !downloadCourseStatus.isLoading &&
      downloadCourseStatus.isSuccess &&
      downloadCourseStatus.data
    ) {
      setModalContext(ModalContext.COPY_COURSE_IN_PROGRESS);
      setIsModalOpen(true);
      setSkip(false);
    }
  }, [downloadCourseStatus, dispatch]);

  if (!videoLibraryData) {
    return <Spinner />;
  }

  return (
    <>
      <Modal
        id="LearnMore"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title={
          modalContext === ModalContext.COPY_COURSE_IN_PROGRESS
            ? Translations.courseLibrary.copyCourse.inProgressTitle.default
            : 'About this module'
        }
      >
        {modalContext === ModalContext.COPY_COURSE_IN_PROGRESS ? (
          <div className="publish-in-progress-container">
            <div className="publish-in-progress-container__spinner">
              <Spinner size="xl" />
            </div>
            <p>
              {Translations.courseLibrary.copyCourse.inProgressInfo.default}
            </p>
          </div>
        ) : (
          <>
            {/* eslint-disable-next-line jam3/no-sanitizer-with-danger */}
            <div
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(modalContent) }}
            />
          </>
        )}
      </Modal>
      <ListTable ariaLabel="List of Video Library Modules" onSort={handleSort}>
        <ListTable.Header>
          <ListTable.Column label={tableColumns.name.label} sortable={true} />
          <ListTable.Column
            label={tableColumns.category.label}
            sortable={false}
          />
          <ListTable.Column
            label={tableColumns.modifiedDate.label}
            sortable={false}
          />
        </ListTable.Header>
        <ListTable.Body>
          {videoLibraryData.elearnings.map((elearning, index) => (
            <ListTable.Tr
              key={elearning.elearningId}
              icon={
                !canDownloadVideoLibrary && (
                  // eslint-disable-next-line jsx-a11y/anchor-is-valid
                  <Link
                    url="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLearnMoreClick(elearning.elearningInfo);
                    }}
                  >
                    Learn more
                  </Link>
                )
              }
              actions={
                canDownloadVideoLibrary && [
                  {
                    icon: <RemoveRedEyeOutlinedIcon />,
                    label: 'Preview',
                    onClick: () => {
                      const windowFeatures = `height=${elearning.windowHeight},width=${elearning.windowWidth}`;
                      window.open(
                        elearning.elearningPath,
                        '_blank',
                        windowFeatures,
                      );
                    },
                  },
                  {
                    icon: <SaveAltIcon />,
                    label: 'Copy to My Library',
                    onClick: () => {
                      handleDownloadCourse(`${elearning.elearningId}`);
                    },
                    withDivider: true,
                  },
                  {
                    icon: <MenuIcon />,
                    label: 'Learn more',
                    onClick: () => {
                      handleLearnMoreClick(elearning.elearningInfo);
                    },
                  },
                ]
              }
            >
              <ListTable.Td>{elearning.elearningName}</ListTable.Td>
              <ListTable.Td>
                <span
                  dangerouslySetInnerHTML={{
                    __html: sanitizeHTML(elearning.elearningCategory),
                  }}
                />
              </ListTable.Td>
              <ListTable.Td>{elearning.publishedTime}</ListTable.Td>
            </ListTable.Tr>
          ))}
        </ListTable.Body>
        <ListTable.Pagination
          hideDropdown={true}
          pageSize={pageSize}
          currentPage={currentQueryString?.page || 1}
          onPageChange={(pageNumber) => {
            /* This is intentional */
            pushParamsToUrl({ page: pageNumber }, history);
          }}
          onPageSizeChange={() => {}}
          totalResults={videoLibraryData?.count}
        />
      </ListTable>
    </>
  );
};

export default VideoLibraryList;
